/**
 * KindAwareSettingsPanel — inline editor for the EventDashboard.
 *
 * Lets organisers tweak the offering-specific rules (party size, deposit,
 * seating, dress code, duration, group size, etc.) AND the public-facing
 * name + description WITHOUT leaving the dashboard. Reads/writes through
 * the same PUT /api/hosted-events/{id} endpoint the form editor uses.
 *
 * One tab per dashboard. Mounted only for experience/reservation kinds —
 * classic ticketed events keep their dedicated Ticket Tiers panel.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, Save, Sparkles, Utensils, X } from 'lucide-react';
import { ExperienceFields, ReservationFields } from './EventBookingFields';
import OrganizerEditor from './OrganizerEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankFromEvent = (event) => ({
  // Public-facing identity (the "Tock card" surface)
  name: event?.name || '',
  tagline: event?.tagline || '',
  short_description: event?.short_description || '',
  description: event?.description || '',
  image_url: event?.image_url || '',
  // Reservation-specific
  party_size_min: event?.party_size_min ?? '',
  party_size_max: event?.party_size_max ?? '',
  large_party_threshold: event?.large_party_threshold ?? '',
  deposit_amount_cents: event?.deposit_amount_cents ?? '',
  turn_time_minutes: event?.turn_time_minutes ?? '',
  dress_code: event?.dress_code || '',
  seating_areas: event?.seating_areas || [],
  // Experience-specific
  duration_minutes: event?.duration_minutes ?? '',
  max_group_size: event?.max_group_size ?? '',
  what_to_bring: event?.what_to_bring || '',
  cancellation_policy: event?.cancellation_policy || '',
  meeting_point: event?.meeting_point || '',
});

export default function KindAwareSettingsPanel({ event, eventId, onSaved }) {
  const kind = event?.kind || 'event';
  const isExperience  = kind === 'experience';
  const isReservation = kind === 'reservation';

  const [formData, setFormData] = useState(() => blankFromEvent(event));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Resync when the parent reloads the event (e.g. after a save).
  useEffect(() => { setFormData(blankFromEvent(event)); setDirty(false); }, [event]);

  // Helper: small wrapper so child Fields components can drive `dirty`.
  const setForm = (next) => {
    const value = typeof next === 'function' ? next(formData) : next;
    setFormData(value);
    setDirty(true);
  };

  if (!isExperience && !isReservation) return null;

  const titleIcon  = isExperience ? Sparkles : Utensils;
  const TitleIcon  = titleIcon;
  const kindLabel  = isExperience ? 'experience' : 'reservation';
  const titleText  = isExperience ? 'Experience settings' : 'Reservation settings';
  const subtitle   = isExperience
    ? 'Public identity + booking rules. Slots live on the Slots & Pricing tab.'
    : 'Public identity + booking rules. Live bookings live on the Bookings tab.';

  const save = async () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      // Strip empty strings for numeric fields so backend doesn't reject
      // them; coerce numeric where set.
      const payload = { ...formData };
      ['party_size_min','party_size_max','large_party_threshold',
       'deposit_amount_cents','turn_time_minutes','duration_minutes',
       'max_group_size'].forEach((k) => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
        else if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      const res = await axios.put(`${API}/hosted-events/${eventId}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` },
      });
      toast.success('Settings saved');
      setDirty(false);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <TitleIcon className={`h-5 w-5 mt-1 flex-shrink-0 ${isExperience ? 'text-fuchsia-400' : 'text-emerald-400'}`} />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold">{titleText}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="settings-save"
          className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold flex-shrink-0"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
          {dirty ? 'Save changes' : 'Saved'}
        </Button>
      </div>

      {/* Organizer profile — edit inline; changes flow to every event's
          public Organiser tab on next load. */}
      <OrganizerEditor defaultOpen={false} />

      {/* ── Public identity (Tock-style card surface) ───────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Public listing</p>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            This is what guests see on the venue page card and in the booking sheet —
            a clear name, an inviting one-liner, and a longer description.
          </p>
          <div className="space-y-2">
            <Label className="text-xs">Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setForm({ ...formData, name: e.target.value })}
              placeholder={isExperience ? 'Wine and Chocolate Pairing' : 'Indoor Reservation'}
              data-testid="settings-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Tagline (one-liner)</Label>
            <Input
              value={formData.tagline}
              onChange={(e) => setForm({ ...formData, tagline: e.target.value })}
              placeholder={isExperience ? 'A romance as old as time — four pairings.' : 'Please make a reservation below…'}
              data-testid="settings-tagline"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Description</Label>
            <Textarea
              value={formData.short_description}
              onChange={(e) => setForm({ ...formData, short_description: e.target.value })}
              placeholder={isExperience
                ? 'Try four wines and four paired chocolates from a local chocolatier…'
                : 'Please make a reservation below or contact us directly at hello@yourvenue.com'}
              className="min-h-[80px]"
              data-testid="settings-short-description"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Cover image URL</Label>
            {formData.image_url && (
              <div className="relative w-full h-32 rounded-lg overflow-hidden bg-muted">
                <img src={formData.image_url} alt="" className="w-full h-full object-cover" />
                <Button
                  type="button" size="icon" variant="ghost"
                  className="absolute top-1 right-1 h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
                  onClick={() => setForm({ ...formData, image_url: '' })}
                  data-testid="settings-image-remove"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
            <Input
              value={formData.image_url}
              onChange={(e) => setForm({ ...formData, image_url: e.target.value })}
              placeholder="https://… (or upload via Basic Info → Cover photo on full editor)"
              data-testid="settings-image-url"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Kind-specific rules ────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {isExperience ? 'Experience rules' : 'Reservation rules'}
          </p>
          {isExperience
            ? <ExperienceFields formData={formData} setFormData={setForm} />
            : <ReservationFields formData={formData} setFormData={setForm} />}
        </CardContent>
      </Card>

      {/* Sticky save reminder when dirty */}
      {dirty && (
        <div className="sticky bottom-4 flex justify-end" data-testid="settings-sticky-save">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold shadow-xl"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save {kindLabel} settings
          </Button>
        </div>
      )}
    </div>
  );
}
